import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Dialog } from 'primereact/dialog';
import { useGetManagerGroupsQuery, useNotifyManagerGroupMutation } from '../../api/groupApi';
import { useGetManagerRequestsQuery, useRequestGroupEnableMutation } from '../../api/requestApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import { selectCurrentUser } from '../../slices/authSlice';
import Badge from '../../components/Badge/Badge';
import MessageField from '../../components/MessageField/MessageField';
import { messageMaxLengthForChannels } from '../../utils/messageLimits';
import useGroupActivityDots from '../../hooks/useGroupActivityDots';
import StatusDots from '../../components/StatusDots/StatusDots';
import GroupQrDialog from './GroupQrDialog';
import s from '../../styles/shared/page.module.css';
import buttons from '../../styles/shared/buttons.module.css';
import table from '../../styles/shared/table.module.css';

export default function GroupsPage() {
  const navigate = useNavigate();
  const showToast = useToast();
  const user = useSelector(selectCurrentUser);
  const [qrGroup, setQrGroup] = useState(null);
  const [notifyGroupTarget, setNotifyGroupTarget] = useState(null);
  const [message, setMessage] = useState('');
  const { activeGroupIds, clear: clearActivityDot } = useGroupActivityDots();

  const {
    data: groups,
    isLoading,
    isError,
    error,
  } = useGetManagerGroupsQuery();
  const { data: pendingRequests } = useGetManagerRequestsQuery('pending');
  const [requestGroupEnable] = useRequestGroupEnableMutation();
  const [notifyGroup, { isLoading: isSendingNotification }] = useNotifyManagerGroupMutation();

  const pendingGroupIds = useMemo(
    () => new Set((pendingRequests ?? []).filter((r) => r.group_id).map((r) => r.group_id)),
    [pendingRequests],
  );
  const pendingCreateCount = useMemo(
    () => (pendingRequests ?? []).filter((r) => r.type === 'group_create').length,
    [pendingRequests],
  );

  useEffect(() => {
    if (isError) showToast('error', 'Groups', errMsg(error, 'Could not load groups.'));
  }, [isError, error, showToast]);

  const tooltipRef = useRef(null);
  useEffect(() => {
    tooltipRef.current?.updateTargetEvents();
  }, [groups]);

  async function handleToggleEnabled(group) {
    try {
      await requestGroupEnable({ groupId: group.id, enabled: !group.enabled }).unwrap();
      showToast('success', 'Groups', 'Change sent to admin for approval.');
    } catch (err) {
      showToast('error', 'Groups', errMsg(err, 'Could not submit request.'));
    }
  }

  function openNotifyDialog(group) {
    setNotifyGroupTarget(group);
    setMessage('');
  }

  async function handleSendNotification() {
    try {
      const res = await notifyGroup({ groupId: notifyGroupTarget.id, message: message.trim() }).unwrap();
      const a = res.audience;
      showToast(
        'success',
        'Groups',
        `Notification sent — email: ${a.email}, sms: ${a.sms}, telegram: ${a.telegram}.`,
      );
      setNotifyGroupTarget(null);
    } catch (err) {
      showToast('error', 'Groups', errMsg(err, 'Could not send notification.'));
    }
  }

  const goEdit = (group) => navigate(`${group.id}/edit`);
  const goSubscribers = (group) => navigate(`${group.id}/subscribers`);
  const goMessages = (group) => {
    clearActivityDot(group.id);
    navigate(`${group.id}/messages`);
  };

  const actionBodyTemplate = (group) => (
    <div className={table.actions}>
      <Button icon="pi pi-pencil" text className={table.iconNeutral} onClick={() => goEdit(group)} />
      <Button icon="pi pi-users" text className={table.iconNeutral} onClick={() => goSubscribers(group)} />
      <Button icon="pi pi-qrcode" text className={table.iconNeutral} onClick={() => setQrGroup(group)} />
      <Button icon="pi pi-megaphone" text className={table.iconNeutral} onClick={() => openNotifyDialog(group)} />
      <span className={table.iconWithDot}>
        <Button icon="pi pi-comments" text className={table.iconNeutral} onClick={() => goMessages(group)} />
        {activeGroupIds.has(group.id) && <span className={table.newMessageDot} />}
      </span>
      <Button
        icon={`pi ${group.enabled ? 'pi-ban' : 'pi-check'}`}
        text
        disabled={pendingGroupIds.has(group.id)}
        className={group.enabled ? table.iconDisable : table.iconSuccess}
        onClick={() => handleToggleEnabled(group)}
      />
    </div>
  );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>Groups</h1>
          <p className={`${s.subtitle} textMuted`}>Alert groups for your organization.</p>
        </div>
        <Button
          label="Request new group"
          icon="pi pi-plus"
          className={buttons.btnPrimary}
          onClick={() => navigate('requests/create')}
        />
      </div>

      {pendingCreateCount > 0 && (
        <p className={`${s.subtitle} textMuted mb-3`}>
          {pendingCreateCount} group creation {pendingCreateCount === 1 ? 'request' : 'requests'} awaiting admin approval.
        </p>
      )}

      <div className={`${table.tableWrapper} ${table.reverseCard}`}>
        <Tooltip ref={tooltipRef} target={`.${table.statusDot}`} position="left" />
        <DataTable
          value={groups ?? []}
          loading={isLoading}
          emptyMessage="No groups yet — ask an admin to create one."
          responsiveLayout="stack"
        >
          <Column
            field="name"
            header="Name"
            className={table.contactCol}
            body={(g) => (
              <div className={table.cellInsider} style={{ gap: '8px' }}>
                {g.name}
                {pendingGroupIds.has(g.id) && (
                  <Badge background="var(--color-badge-warn-bg)" color="var(--color-badge-warn-text)">
                    pending change
                  </Badge>
                )}
              </div>
            )}
          />
          <Column
            header="Channels"
            className={`${s.mutedCell} ${table.contactCol}`}
            body={(g) => (
              <div className={table.cellInsider}>
                {(g.channels ?? []).filter((c) => c !== 'push').join(', ') || '—'}
                {!g.broadcast && ' · chat'}
              </div>
            )}
          />
          <Column
            header="Status"
            className={table.statusCol}
            style={{ width: '130px' }}
            body={(g) => (
              <div className={table.cellInsider}>
                <div className={`${table.statusBadgesDesktop} ${g.enabled ? s.statusOk : s.statusDisabled}`}>
                  {g.enabled ? 'enabled' : 'disabled'}
                </div>
                <StatusDots
                  items={[
                    {
                      label: g.enabled ? 'enabled' : 'disabled',
                      color: g.enabled ? 'var(--color-success)' : 'var(--color-error)',
                    },
                  ]}
                />
              </div>
            )}
          />
          <Column
            header="Actions"
            className={table.actionsCol}
            body={actionBodyTemplate}
            style={{ width: '360px' }}
          />
        </DataTable>
      </div>

      <GroupQrDialog group={qrGroup} organizationSlug={user?.organization_slug} onHide={() => setQrGroup(null)} />

      <Dialog
        header={`Send notification${notifyGroupTarget ? ` — ${notifyGroupTarget.name}` : ''}`}
        visible={Boolean(notifyGroupTarget)}
        onHide={() => setNotifyGroupTarget(null)}
        style={{ width: '520px' }}
        modal
      >
        <MessageField
          id="message"
          value={message}
          onChange={setMessage}
          maxLength={messageMaxLengthForChannels(notifyGroupTarget?.channels)}
        />
        <Button
          label="Send"
          className={buttons.saveBtn}
          loading={isSendingNotification}
          disabled={!message.trim()}
          onClick={handleSendNotification}
        />
      </Dialog>
    </div>
  );
}
